import * as React from 'react'
import { FileDown, Loader2 } from 'lucide-react'
import type { ProposalPdfDto } from '@lib/dto/proposal-media.dto'

/** A download, not a navigation: storage answers as an attachment. */
function openDownload(url: string): void {
  window.location.assign(url)
}

/**
 * Download the proposal as the PDF the API had printed.
 *
 * The first download of a new PDF takes a few seconds while it is made, so
 * the button says so. Where PDFs are not set up, or one could not be made,
 * it opens the browser's own print instead, which can still save a PDF: a
 * client asking for a copy always gets one.
 */
export function PdfButton({
  load,
  onDownload,
  download = openDownload,
}: {
  load: () => Promise<ProposalPdfDto>
  onDownload?: () => void
  /** How the file is fetched once it exists. Replaced only in tests. */
  download?: (url: string) => void
}) {
  const [isPreparing, setPreparing] = React.useState(false)

  async function handleClick(): Promise<void> {
    setPreparing(true)
    onDownload?.()
    try {
      const { url } = await load()
      download(url)
    } catch {
      window.print()
    } finally {
      setPreparing(false)
    }
  }

  return (
    <button
      type="button"
      className="proposal-topbar-button"
      disabled={isPreparing}
      onClick={() => {
        void handleClick()
      }}
    >
      {isPreparing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <FileDown className="h-3.5 w-3.5" aria-hidden />
      )}
      <span className="hidden sm:inline">
        {isPreparing ? 'Preparing the PDF…' : 'Download PDF'}
      </span>
      <span className="sm:hidden">PDF</span>
    </button>
  )
}
